"use client";

import { useEffect, useState } from "react";

/* Reactive canvas behind every page: two-pass WebGL2, no libraries.
   Pass 1 accumulates a decaying ink trail behind the cursor (ping-pong,
   quarter res). Pass 2 composites a domain-warped noise field over the
   theme canvas color, lit by ambient breathing, the trail, and scroll
   velocity. Dark theme lifts luminance off the near-black (phosphor
   tinted), light theme presses graphite into the paper.
   Validated in design/prototypes/reactive-shader.html — tune there first,
   then mirror the numbers here. */

const FIELD = {
  ambient: 0.34,
  trail: 0.55,
  scroll: 0.5,
  phosphor: 0.18,
  grain: 0.5,
  tempo: 1.0,
};

const QUAD = `#version 300 es
in vec2 aPos;
out vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const TRAIL_FS = `#version 300 es
precision highp float;
uniform sampler2D uPrev;
uniform vec2 uRes;
uniform float uAspect;
uniform vec2 uPoint;
uniform float uInject;
uniform float uRadius;
in vec2 vUv;
out vec4 frag;
void main() {
  vec2 px = 1.0 / uRes;
  float c = texture(uPrev, vUv).r;
  float blur =
    texture(uPrev, vUv + vec2(px.x, 0.0)).r +
    texture(uPrev, vUv - vec2(px.x, 0.0)).r +
    texture(uPrev, vUv + vec2(0.0, px.y)).r +
    texture(uPrev, vUv - vec2(0.0, px.y)).r;
  float v = mix(c, blur * 0.25, 0.4);
  v = max(v * 0.962 - 0.0012, 0.0);
  vec2 a = vec2(uAspect, 1.0);
  float d = distance(vUv * a, uPoint * a);
  v += uInject * exp(-(d * d) / (uRadius * uRadius));
  frag = vec4(v, 0.0, 0.0, 1.0);
}`;

const COMP_FS = `#version 300 es
precision highp float;
uniform sampler2D uTrail;
uniform vec2 uRes;
uniform float uAspect;
uniform float uTime;
uniform float uFrame;
uniform float uAmbient;
uniform float uTrailAmt;
uniform float uScroll;
uniform float uPhosphor;
uniform float uGrain;
uniform vec3 uBase;
uniform float uLightMode;
in vec2 vUv;
out vec4 frag;

float hash(vec2 p) {
  p = fract(p * vec2(234.34, 435.345));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 3; i++) {
    v += a * vnoise(p);
    p = p * 2.03 + vec2(11.7, 5.1);
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = vUv * vec2(uAspect, 1.0);
  float t = uTime;

  vec2 warp = vec2(
    fbm(uv * 1.4 + t * 0.020),
    fbm(uv * 1.4 - t * 0.016 + 7.31));
  float n = fbm(uv * 2.1 + (warp - 0.5) * 1.3 + vec2(0.0, t * 0.012));
  float field = n - 0.5;

  float trail = texture(uTrail, vUv).r;

  float breathe = 0.85 + 0.15 * sin(t * 0.25);
  float energy = uAmbient * breathe + uScroll;
  float lum = field * energy * 0.16
            + trail * uTrailAmt * 0.22 * (0.55 + 0.45 * n);

  vec3 col;
  if (uLightMode > 0.5) {
    float fiber = (vnoise(uv * 420.0) - 0.5) * 0.012;
    col = uBase + fiber - max(lum, field * energy * 0.05) * vec3(1.0, 0.98, 0.95);
  } else {
    vec3 tint = mix(vec3(1.0, 0.99, 0.96), vec3(0.55, 1.0, 0.6), uPhosphor);
    col = uBase + lum * tint;
  }

  float g = hash(vUv * uRes + fract(uFrame * 0.618) * 71.0) - 0.5;
  col += g * (0.014 * uGrain + 0.004);

  frag = vec4(col, 1.0);
}`;

const hexToRgb = (hex: string): [number, number, number] => {
  const v = parseInt(hex.replace("#", ""), 16);
  return [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255];
};

export const ReactiveField = () => {
  const [motionOk, setMotionOk] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setMotionOk(!mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const active = motionOk;

  useEffect(() => {
    if (!active) return;
    /* Heavy init (context creation, shader compile/link) waits for idle:
       run at hydration it lands squarely in the load's main-thread window,
       and the field is imperceptible until the cursor feeds it anyway. */
    let disposed = false;
    let cleanup: (() => void) | undefined;

    const init = async () => {
      if (disposed) return;
      const canvas = document.getElementById(
        "reactive-field",
      ) as HTMLCanvasElement | null;
      if (!canvas) return;
      /* An opaque context shows black until its first draw, and the draw
         now waits on the async compile below: hold the canvas back until
         the first frame lands (the body's canvas color shows meanwhile). */
      canvas.style.visibility = "hidden";
      const gl = canvas.getContext("webgl2", {
        antialias: false,
        alpha: false,
      });
      if (!gl) return;

      const compile = (vsSrc: string, fsSrc: string) => {
        const p = gl.createProgram()!;
        for (const [type, src] of [
          [gl.VERTEX_SHADER, vsSrc],
          [gl.FRAGMENT_SHADER, fsSrc],
        ] as const) {
          const s = gl.createShader(type)!;
          gl.shaderSource(s, src);
          gl.compileShader(s);
          gl.attachShader(p, s);
        }
        gl.linkProgram(p);
        return p;
      };

      /* Any status query right after compile/link blocks the main thread
         until the GPU is done: 1-2s (up to 10s) under software GL, landing
         in the load's blocking-time window. With KHR_parallel_shader_compile
         the completion flag is polled once per frame instead, and the
         status read only happens when it's free. */
      const linked = (progs: WebGLProgram[]) =>
        new Promise<boolean>((resolve) => {
          const ext = gl.getExtension("KHR_parallel_shader_compile");
          const check = () => {
            if (disposed) return resolve(false);
            if (
              ext &&
              !progs.every((p) =>
                gl.getProgramParameter(p, ext.COMPLETION_STATUS_KHR),
              )
            ) {
              requestAnimationFrame(check);
              return;
            }
            resolve(
              progs.every((p) => gl.getProgramParameter(p, gl.LINK_STATUS)),
            );
          };
          check();
        });

      const trailProg = compile(QUAD, TRAIL_FS);
      const compProg = compile(QUAD, COMP_FS);
      if (!(await linked([trailProg, compProg])) || disposed)
        return; /* stay on the plain canvas color */

      const vao = gl.createVertexArray();
      gl.bindVertexArray(vao);
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 3, -1, -1, 3]),
        gl.STATIC_DRAW,
      );
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

      let W = 0,
        H = 0,
        TW = 0,
        TH = 0,
        flip = 0;
      let texs: WebGLTexture[] = [];
      let fbos: WebGLFramebuffer[] = [];

      const makeTrailTargets = () => {
        for (const t of texs) gl.deleteTexture(t);
        for (const f of fbos) gl.deleteFramebuffer(f);
        texs = [];
        fbos = [];
        for (let i = 0; i < 2; i++) {
          const tex = gl.createTexture()!;
          gl.bindTexture(gl.TEXTURE_2D, tex);
          gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.RGBA8,
            TW,
            TH,
            0,
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            null,
          );
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          const fbo = gl.createFramebuffer()!;
          gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
          gl.framebufferTexture2D(
            gl.FRAMEBUFFER,
            gl.COLOR_ATTACHMENT0,
            gl.TEXTURE_2D,
            tex,
            0,
          );
          texs.push(tex);
          fbos.push(fbo);
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      };

      const resize = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        W = Math.round(innerWidth * dpr);
        H = Math.round(innerHeight * dpr);
        canvas.width = W;
        canvas.height = H;
        TW = Math.max(2, W >> 2);
        TH = Math.max(2, H >> 2);
        makeTrailTargets();
      };
      resize();

      /* Theme is read once per change, not per frame. */
      let base = hexToRgb(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--oi-canvas")
          .trim() || "#181411",
      );
      let lightMode =
        document.documentElement.dataset.theme === "light" ? 1 : 0;
      const mo = new MutationObserver(() => {
        base = hexToRgb(
          getComputedStyle(document.documentElement)
            .getPropertyValue("--oi-canvas")
            .trim() || "#181411",
        );
        lightMode = document.documentElement.dataset.theme === "light" ? 1 : 0;
      });
      mo.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme"],
      });

      const pointer = {
        x: 0.5,
        y: 0.5,
        tx: 0.5,
        ty: 0.5,
        speed: 0,
        has: false,
      };
      let pulse = 0;
      let scrollEnergy = 0;
      let lastScrollY = window.scrollY;
      let lastPX: number | null = null;
      let lastPY = 0;

      const onPointerMove = (e: PointerEvent) => {
        pointer.tx = e.clientX / innerWidth;
        pointer.ty = 1 - e.clientY / innerHeight;
        if (lastPX !== null) {
          const d = Math.hypot(e.clientX - lastPX, e.clientY - lastPY);
          pointer.speed = Math.min(1, pointer.speed + d / 90);
        }
        lastPX = e.clientX;
        lastPY = e.clientY;
        pointer.has = true;
      };
      const onPointerDown = (e: PointerEvent) => {
        pointer.tx = pointer.x = e.clientX / innerWidth;
        pointer.ty = pointer.y = 1 - e.clientY / innerHeight;
        pointer.has = true;
        pulse = 1.4;
      };
      const onScroll = () => {
        const dy = Math.abs(window.scrollY - lastScrollY);
        lastScrollY = window.scrollY;
        scrollEnergy = Math.min(1, scrollEnergy + dy / 900);
      };

      const uT: Record<string, WebGLUniformLocation | null> = {};
      const uC: Record<string, WebGLUniformLocation | null> = {};
      for (const name of [
        "uPrev",
        "uRes",
        "uAspect",
        "uPoint",
        "uInject",
        "uRadius",
      ])
        uT[name] = gl.getUniformLocation(trailProg, name);
      for (const name of [
        "uTrail",
        "uRes",
        "uAspect",
        "uTime",
        "uFrame",
        "uAmbient",
        "uTrailAmt",
        "uScroll",
        "uPhosphor",
        "uGrain",
        "uBase",
        "uLightMode",
      ])
        uC[name] = gl.getUniformLocation(compProg, name);

      let raf = 0;
      let time = 0;
      let frame = 0;
      let last = performance.now();

      const loop = (now: number) => {
        const dt = Math.min(0.05, (now - last) / 1000);
        last = now;
        time += dt * FIELD.tempo;
        frame++;

        pointer.x += (pointer.tx - pointer.x) * 0.11;
        pointer.y += (pointer.ty - pointer.y) * 0.11;
        pointer.speed *= 0.86;
        pulse *= 0.8;
        scrollEnergy *= 0.94;

        const inject =
          (pointer.has ? pointer.speed * 0.5 * FIELD.trail : 0) + pulse * 0.6;

        gl.bindFramebuffer(gl.FRAMEBUFFER, fbos[1 - flip]);
        gl.viewport(0, 0, TW, TH);
        gl.useProgram(trailProg);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texs[flip]);
        gl.uniform1i(uT.uPrev, 0);
        gl.uniform2f(uT.uRes, TW, TH);
        gl.uniform1f(uT.uAspect, W / H);
        gl.uniform2f(uT.uPoint, pointer.x, pointer.y);
        gl.uniform1f(uT.uInject, inject);
        gl.uniform1f(uT.uRadius, 0.075 + pulse * 0.05);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        flip = 1 - flip;

        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, W, H);
        gl.useProgram(compProg);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texs[flip]);
        gl.uniform1i(uC.uTrail, 0);
        gl.uniform2f(uC.uRes, W, H);
        gl.uniform1f(uC.uAspect, W / H);
        gl.uniform1f(uC.uTime, time);
        gl.uniform1f(uC.uFrame, frame);
        gl.uniform1f(uC.uAmbient, FIELD.ambient);
        gl.uniform1f(uC.uTrailAmt, FIELD.trail);
        gl.uniform1f(uC.uScroll, scrollEnergy * FIELD.scroll * 0.6);
        gl.uniform1f(uC.uPhosphor, FIELD.phosphor);
        gl.uniform1f(uC.uGrain, FIELD.grain);
        gl.uniform3f(uC.uBase, base[0], base[1], base[2]);
        gl.uniform1f(uC.uLightMode, lightMode);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        if (frame === 1) canvas.style.visibility = "";

        raf = requestAnimationFrame(loop);
      };

      const onVisibility = () => {
        cancelAnimationFrame(raf);
        if (!document.hidden) {
          last = performance.now();
          raf = requestAnimationFrame(loop);
        }
      };

      window.addEventListener("pointermove", onPointerMove, { passive: true });
      window.addEventListener("pointerdown", onPointerDown, { passive: true });
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", resize);
      document.addEventListener("visibilitychange", onVisibility);
      raf = requestAnimationFrame(loop);

      cleanup = () => {
        cancelAnimationFrame(raf);
        mo.disconnect();
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerdown", onPointerDown);
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", resize);
        document.removeEventListener("visibilitychange", onVisibility);
        for (const t of texs) gl.deleteTexture(t);
        for (const f of fbos) gl.deleteFramebuffer(f);
        gl.deleteProgram(trailProg);
        gl.deleteProgram(compProg);
        gl.deleteBuffer(buf);
        gl.deleteVertexArray(vao);
      };
    };

    const useIdle = typeof window.requestIdleCallback === "function";
    const handle = useIdle
      ? window.requestIdleCallback(init, { timeout: 1200 })
      : window.setTimeout(init, 250);

    return () => {
      disposed = true;
      if (useIdle) window.cancelIdleCallback(handle);
      else clearTimeout(handle);
      cleanup?.();
    };
  }, [active]);

  if (!active) return null;

  return (
    <canvas
      id="reactive-field"
      aria-hidden
      /* body { isolation: isolate } makes -1 sit above the body background
         but below all content, including non-positioned elements. */
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: -1,
      }}
    />
  );
};
